import { MouseEventHandler, useState } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import { Box, IconButton, makeStyles, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { formatDate } from '@roc/feature-utils';
import { PhotoGalleryImage, PhotoGalleryModal, PhotoCover } from '@roc/ui';
import { ReactComponent as FullScreenIcon } from '../../assets/fullScreen.svg';
import { PhotoMetadata } from '../../components/photoMetadata';
import { GalleryPhotosMap } from './galleryPhotosMap';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: '1rem',
    height: '100%',
    width: '100%',
  },
  scrollable: {
    overflowY: 'auto',
    overflowX: 'hidden',
    flexGrow: 1,
    padding: '0',
  },
  overlayPhotoContainer: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 15,
    display: 'flex',
    gap: '8px',
  },
  photoDetailButton: {
    backgroundColor: 'black',
    borderRadius: '5px',
    padding: '7px',
  },
  photoDetailIcon: {
    color: 'white',
    height: '20px',
    width: '20px',
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    maxWidth: '100%',
  },
  miniMap: {
    height: '260px',
    width: '100%',
    borderRadius: '16px',
    overflow: 'hidden',
  },
}));

export const GalleryPhotoDetail = observer(() => {
  const classes = useStyles();
  const { inspectionStore } = useStore();
  const { galleryStore } = inspectionStore;
  const { galleryPhotosStore, selectedUser, selectedFolder } = galleryStore;
  const [photosModalOpen, setPhotosModalOpen] = useState(false);

  const photo = galleryPhotosStore.photoSelected;
  if (!photo) {
    return null;
  }

  const captureDate = photo.captureTimeIso ? new Date(photo.captureTimeIso) : null;

  const modalPhotos: PhotoGalleryImage[] = galleryPhotosStore.photos.map(p => ({
    key: p.uuid,
    url: p.image,
    preferredRotation: p.preferredRotation,
    caption: p.caption,
  }));

  const openFullScreen: MouseEventHandler<Element> = e => {
    setPhotosModalOpen(true);
    galleryPhotosStore.fetchPhoto(selectedUser?.userId, photo);
    e.preventDefault();
    e.stopPropagation();
  };

  const onPhotoChange = (key: string) => {
    const next = galleryPhotosStore.photos.find(p => p.uuid === key);
    if (next) {
      galleryPhotosStore.fetchPhoto(selectedUser?.userId, next);
    }
  };

  const onPhotoRotation = (key: string, rotationDegrees: number) => {
    galleryPhotosStore.setPhotoPreferredRotation(key, rotationDegrees);
  };

  return (
    <>
      <Box className={classes.root}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          position="sticky"
          top={0}
          bgcolor="#fff"
          zIndex={100}
          paddingY={2}
        >
          <Box display="flex" flexDirection="column" maxWidth="calc(100% - 48px)">
            <Typography variant="h5" className={classes.ellipsis}>
              {photo.caption || selectedFolder?.name || 'Photo'}
            </Typography>
            <Typography variant="body2">
              Captured on: {formatDate(captureDate)}
            </Typography>
          </Box>
          <IconButton
            onClick={() => galleryPhotosStore.selectPhoto(selectedUser?.userId, null)}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box
          className={classes.scrollable}
          display="flex"
          flexDirection="column"
          gridGap="2rem"
        >
          <Box style={{ position: 'relative' }}>
            <Box className={classes.overlayPhotoContainer}>
              <IconButton className={classes.photoDetailButton} onClick={openFullScreen}>
                <FullScreenIcon className={classes.photoDetailIcon} />
              </IconButton>
            </Box>
            <PhotoCover
              url={photo.image}
              rotationDegrees={photo.preferredRotation || 0}
              width="100%"
              height="400px"
              borderRadius="16px"
              onClick={openFullScreen}
              caption={photo.caption}
            />
          </Box>

          {photo.locationData && (
            <Box className={classes.miniMap}>
              <GalleryPhotosMap photos={[photo]} selectedPhotoUuid={photo.uuid} />
            </Box>
          )}

          {photo.deviceData && photo.locationData && (
            <PhotoMetadata inspectionPhoto={photo as any} />
          )}
        </Box>
      </Box>

      <PhotoGalleryModal
        open={photosModalOpen}
        onClose={() => setPhotosModalOpen(false)}
        photos={modalPhotos}
        enableZoom
        selectedPhotoKey={photo.uuid}
        title="Photos"
        onPhotoRotation={onPhotoRotation}
        onPhotoChange={onPhotoChange}
      />
    </>
  );
});
